import { ChevronDown } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'

import type { CarrierSummary } from './legCarriers'

/** A leg's carriers in words — «Silk Road Trans LLC», «2 перевозчика ▾» with
 *  the names behind a disclosure, or «Перевозчик скрыт» (S-10, AC #9).
 *
 *  The rule is `legCarriers`'; this only says it. The disclosure is a real
 *  button with `aria-expanded`, so the count is never the only thing a reader
 *  can reach — the names are one press away for a keyboard and a screen reader
 *  alike. `placement` decides where the names open: under the summary line on
 *  the leg page (`popover`, over what follows, which is the in-page `z-40`
 *  layer), or in the flow of a card whose frame clips anything that floats
 *  (`inline`). */
export function LegCarriersLine({
  summary,
  placement = 'popover',
  className,
}: {
  summary: CarrierSummary
  placement?: 'popover' | 'inline'
  className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const listId = useId()

  if (summary.kind === 'none') return null
  if (summary.kind === 'hidden') {
    return <span className={className}>{t('common.suppressed.carrier')}</span>
  }
  if (summary.kind === 'one') {
    return <span className={cn('[overflow-wrap:anywhere]', className)}>{summary.name}</span>
  }

  return (
    <span className={cn('relative inline-flex flex-col', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(!open)}
        className="hover:text-text-heading inline-flex cursor-pointer items-center gap-1"
      >
        {t('cases.legs.carriersCount', { count: summary.names.length })}
        <ChevronDown
          size={13}
          aria-hidden
          className={cn('transition-transform', open && 'rotate-180')}
        />
      </button>
      {open ? (
        <ul
          id={listId}
          aria-label={t('cases.legs.carriersList')}
          className={cn(
            'text-text-body flex flex-col gap-1 text-[13px]',
            placement === 'popover'
              ? 'bg-surface border-border-subtle absolute top-full left-0 z-40 mt-1 min-w-56 rounded-md border px-3 py-2'
              : 'mt-1.5',
          )}
        >
          {summary.names.map((name, index) => (
            <li key={`${index}-${name}`} className="[overflow-wrap:anywhere]">
              {name}
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  )
}
